import type { ImportFieldNode } from '@erp/rpc';
import {
  SQL_SCHEMA_FIELD_TYPES,
  SYSTEM_ENTITY_LABELS,
  SystemEntity,
  type SchemaFieldIndexType,
  type SqlSchemaFieldType,
} from '@erp/shared-types';
import {
  getArrayItemTypeSelection,
  normalizeSchemaFieldKey,
  sqlDefaultValueConstraintError,
} from '@erp/shared-utils';
import { isDefaultValueValid } from './DefaultValueInput';

export const FRIENDLY_FIELD_TYPES = [
  'string',
  'text',
  'integer',
  'float',
  'boolean',
  'json',
  'datetime',
  'date',
  'array',
  'object',
  'entity_reference',
  'document',
] as const;

const ENTITY_REFERENCE_TARGETS: ReadonlySet<string> = new Set(
  Object.values(SystemEntity),
);

export function isEntityReferenceTarget(value: string): value is SystemEntity {
  return ENTITY_REFERENCE_TARGETS.has(value);
}

/** Options for an `entity_reference` field. Duplicate labels keep the enum id. */
export function entityReferenceOptions(): {
  value: SystemEntity;
  label: string;
}[] {
  const labelCounts = new Map<string, number>();
  for (const entity of Object.values(SystemEntity)) {
    const label = SYSTEM_ENTITY_LABELS[entity];
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }
  return Object.values(SystemEntity)
    .map((value) => {
      const base = SYSTEM_ENTITY_LABELS[value];
      const label =
        (labelCounts.get(base) ?? 0) > 1 ? `${base} (${value})` : base;
      return { value, label };
    })
    .sort((left, right) => left.label.localeCompare(right.label));
}

/**
 * Widens beyond `FRIENDLY_FIELD_TYPES` (the default dropdown list) to also
 * allow "numeric" as a row's `fieldType` — without adding it to the friendly
 * list itself, so it's never offered as a *newly pickable* option (see
 * `SQL_QUICK_ADD_FIELD_TYPES`). A row still needs to be able to *hold* type
 * "numeric" without lying about it, e.g. after importing a "sql" schema field
 * via `sqlSchemaParser` (which does support numeric with precision/scale).
 */
export type FriendlyFieldType =
  (typeof FRIENDLY_FIELD_TYPES)[number] | 'numeric';

/**
 * Quick-add/edit row type options for `schemaType: "sql"` schemas. Excludes
 * "numeric" — unlike every other type here, "numeric" requires a
 * precision/scale pair that this single-row editor has no inputs for, so
 * offering it here would let a user pick a type they can never successfully
 * save. Numeric columns (with exact precision/scale) are added via
 * **Import from JSON** (`sqlSchemaParser`), which does collect precision and
 * scale from the pasted schema.
 */
export const SQL_QUICK_ADD_FIELD_TYPES = ['string', 'integer', 'date'] as const;

export type FriendlyFieldRow = {
  id: string;
  fieldName: string;
  fieldType: FriendlyFieldType;
  isRequired: boolean;
  description: string;
  /**
   * Whether this field declares a column default. Tracked separately from
   * `defaultValue` so an empty string (`''`) can be a real default — the value
   * alone can't distinguish "no default" from "default of empty string".
   */
  hasDefault: boolean;
  /** Column default value (only sent when `hasDefault`). Container types never have one. */
  defaultValue: unknown;
  extractionHints: { aiPromptHint: string } | null;
  children: FriendlyFieldRow[];
  /** When array has no properties yet but user chose object item type. */
  arrayItemMode?: 'object';
  /** Precision/scale for a "numeric" row. Only meaningful when fieldType is "numeric". */
  validationRules?: { precision?: number; scale?: number } | null;
  /**
   * Ingest-column index kinds for a "sql" schema field. Opt-in: only the
   * listed kinds are created. Null/undefined and an empty array both mean no
   * indexes.
   */
  indexes?: SchemaFieldIndexType[] | null;
  /**
   * The system entity an `entity_reference` field points at. Null until the
   * author picks one, and null for every other field type.
   */
  referencedEntityType?: SystemEntity | null;
};

export function isContainerFriendlyType(fieldType: FriendlyFieldType): boolean {
  return fieldType === 'object' || fieldType === 'array';
}

export function newRow(defaultFieldName = ''): FriendlyFieldRow {
  return {
    id: crypto.randomUUID(),
    fieldName: defaultFieldName,
    fieldType: 'string',
    isRequired: false,
    description: '',
    hasDefault: false,
    defaultValue: null,
    extractionHints: null,
    children: [],
  };
}

/**
 * Rows produced by the shared array helpers carry only `fieldName` and
 * `fieldType` (the `item` child for an "array of <scalar>"). Fill in the
 * rest so every walker in this file can rely on the full row shape.
 */
export function completeRow(
  partial: Partial<FriendlyFieldRow> &
    Pick<FriendlyFieldRow, 'fieldName' | 'fieldType'>,
): FriendlyFieldRow {
  return {
    ...newRow(partial.fieldName),
    ...partial,
    children: (partial.children ?? []).map(completeRow),
  };
}

export function defaultChildRowForParent(
  parent: FriendlyFieldRow,
): FriendlyFieldRow {
  const mode =
    parent.fieldType === 'array' ? getArrayItemTypeSelection(parent) : null;
  if (mode === 'object') {
    return newRow(`field_${parent.children.length + 1}`);
  }
  return newRow('');
}

function humanizeKey(fieldName: string): string {
  return fieldName
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Re-exported from shared-utils so the backend can match file columns to schema
 * fields through the exact same rule this builder saves them under.
 */
export const normalizeRootFieldKey = normalizeSchemaFieldKey;

function normalizeNestedFieldKey(raw: string): string {
  let s = raw
    .trim()
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!s) return '';
  if (/^[0-9]/.test(s)) {
    s = `field_${s}`;
  }
  if (!/^[a-zA-Z]/.test(s)) {
    s = `field_${s}`;
  }
  // Name preserved verbatim (sanitized only) — NO snake_case→camelCase
  // conversion. Field names become literal wire keys when the schema drives
  // an outbound request body (ERP submission maps), so `orig_sys_line_ref`
  // must stay `orig_sys_line_ref`. Parsers that WANT camelCase (the Extend
  // AI import) convert before rows reach this tree.
  return s;
}

function acceptedImportFieldName(
  row: FriendlyFieldRow,
  isRoot: boolean,
): string | null {
  const key = isRoot
    ? normalizeRootFieldKey(row.fieldName)
    : normalizeNestedFieldKey(row.fieldName);
  if (!key || !/^[a-zA-Z][a-zA-Z0-9_]*$/u.test(key)) {
    return null;
  }
  if (!isRoot) return key;
  if (key === 'payload' || !/^[a-z][a-z0-9_]*$/u.test(key)) return null;
  return key;
}

/** Defaults are checkbox-gated and never apply to container types. */
function importDefaultValue(row: FriendlyFieldRow): unknown {
  if (!row.hasDefault || isContainerFriendlyType(row.fieldType)) return null;
  // `?? null` only normalizes undefined, so an empty string default is kept.
  return row.defaultValue ?? null;
}

function importNodeExtras(row: FriendlyFieldRow): Partial<ImportFieldNode> {
  const extras: Partial<ImportFieldNode> = {};
  if (row.fieldType === 'numeric') {
    extras.validationRules = row.validationRules ?? null;
  }
  if (row.indexes != null) extras.indexes = row.indexes;
  if (row.fieldType === 'entity_reference' && row.referencedEntityType) {
    extras.referencedEntityType = row.referencedEntityType;
  }
  return extras;
}

export function rowToImportNode(
  row: FriendlyFieldRow,
  isRoot: boolean,
): ImportFieldNode | null {
  const key = acceptedImportFieldName(row, isRoot);
  if (!key) return null;

  const childNodes = row.children
    .map((child) => rowToImportNode(child, false))
    .filter((node): node is ImportFieldNode => node !== null);

  return {
    fieldName: key,
    fieldType: row.fieldType,
    label: humanizeKey(key),
    description: row.description.trim() || null,
    isRequired: row.isRequired,
    enumValues: null,
    defaultValue: importDefaultValue(row),
    extractionHints: row.extractionHints ?? null,
    children: childNodes.length > 0 ? childNodes : undefined,
    ...importNodeExtras(row),
  };
}

export function rowsToImportNodes(rows: FriendlyFieldRow[]): ImportFieldNode[] {
  const out: ImportFieldNode[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const node = rowToImportNode(row, true);
    if (!node) continue;
    if (seen.has(node.fieldName)) continue;
    seen.add(node.fieldName);
    out.push(node);
  }
  return out;
}

function importNodeToRow(node: ImportFieldNode): FriendlyFieldRow {
  const isFriendlyType =
    node.fieldType === 'numeric' ||
    (FRIENDLY_FIELD_TYPES as readonly string[]).includes(node.fieldType);

  return {
    id: crypto.randomUUID(),
    fieldName: node.fieldName,
    fieldType: (isFriendlyType
      ? node.fieldType
      : 'string') as FriendlyFieldType,
    isRequired: node.isRequired,
    description: node.description ?? '',
    hasDefault: node.defaultValue != null,
    defaultValue: node.defaultValue ?? null,
    extractionHints: node.extractionHints ?? null,
    children: (node.children ?? []).map(importNodeToRow),
    ...(node.fieldType === 'numeric'
      ? { validationRules: node.validationRules ?? null }
      : {}),
    ...(node.indexes != null ? { indexes: node.indexes } : {}),
    referencedEntityType: node.referencedEntityType ?? null,
  };
}

export function importNodesToRows(
  nodes: ImportFieldNode[],
): FriendlyFieldRow[] {
  return nodes.map(importNodeToRow);
}

/**
 * Returns rows (anywhere in the tree) whose declared default is invalid for
 * their type — e.g. an enabled default that is blank on a number/date field.
 * Used to block staging/publishing with a clear message.
 */
export function rowsWithInvalidDefault(
  rows: FriendlyFieldRow[],
): FriendlyFieldRow[] {
  const invalid: FriendlyFieldRow[] = [];
  for (const row of rows) {
    if (!isDefaultValueValid(row.fieldType, row.hasDefault, row.defaultValue)) {
      invalid.push(row);
    }
    invalid.push(...rowsWithInvalidDefault(row.children ?? []));
  }
  return invalid;
}

/** Whether a row's precision/scale (only meaningful for "numeric") are set and valid. */
export function isValidNumericRow(row: FriendlyFieldRow): boolean {
  if (row.fieldType !== 'numeric') return true;
  const precision = row.validationRules?.precision;
  const scale = row.validationRules?.scale;
  return (
    typeof precision === 'number' &&
    Number.isInteger(precision) &&
    precision >= 1 &&
    typeof scale === 'number' &&
    Number.isInteger(scale) &&
    scale >= 0 &&
    scale <= precision
  );
}

/**
 * Returns rows (anywhere in the tree) whose "numeric" precision/scale are
 * missing or invalid. Used to block staging/publishing with a clear message
 * — a numeric field with no valid precision/scale would otherwise fail at
 * the backend (`SchemaDefinitionService`) with a less actionable error.
 */
/** Entity fields that have not named which system entity they point at. */
export function rowsMissingReferencedEntity(
  rows: FriendlyFieldRow[],
): FriendlyFieldRow[] {
  const missing: FriendlyFieldRow[] = [];
  for (const row of rows) {
    if (row.fieldType === 'entity_reference' && !row.referencedEntityType) {
      missing.push(row);
    }
    missing.push(...rowsMissingReferencedEntity(row.children ?? []));
  }
  return missing;
}

export function rowsWithInvalidNumericArgs(
  rows: FriendlyFieldRow[],
): FriendlyFieldRow[] {
  const invalid: FriendlyFieldRow[] = [];
  for (const row of rows) {
    if (!isValidNumericRow(row)) {
      invalid.push(row);
    }
    invalid.push(...rowsWithInvalidNumericArgs(row.children ?? []));
  }
  return invalid;
}

const SQL_SCHEMA_FIELD_TYPE_SET: ReadonlySet<string> = new Set(
  SQL_SCHEMA_FIELD_TYPES,
);

function isSqlSchemaFieldType(
  fieldType: FriendlyFieldType,
): fieldType is SqlSchemaFieldType {
  return SQL_SCHEMA_FIELD_TYPE_SET.has(fieldType);
}

/**
 * Whether a row's *default value* (when enabled) satisfies the Postgres
 * column constraints its `fieldType` will provision on a "sql" schema —
 * VARCHAR(255), int4 range, NUMERIC(precision, scale), a real calendar date.
 * A no-op (valid) for rows with no default, or a fieldType outside the sql
 * vocabulary.
 */
export function isValidSqlDefaultValue(row: FriendlyFieldRow): boolean {
  if (!row.hasDefault || row.defaultValue == null) return true;
  if (!isSqlSchemaFieldType(row.fieldType)) return true;
  return (
    sqlDefaultValueConstraintError(
      row.fieldType,
      row.defaultValue,
      row.fieldType === 'numeric' ? row.validationRules : null,
    ) === null
  );
}

/**
 * Returns rows (anywhere in the tree) whose default value doesn't fit its
 * sql column type's constraints. Used to block staging/publishing with a
 * clear message — this would otherwise fail at the backend
 * (`SchemaDefinitionService`) with a less actionable error.
 */
export function rowsWithInvalidSqlDefaultValue(
  rows: FriendlyFieldRow[],
): FriendlyFieldRow[] {
  const invalid: FriendlyFieldRow[] = [];
  for (const row of rows) {
    if (!isValidSqlDefaultValue(row)) {
      invalid.push(row);
    }
    invalid.push(...rowsWithInvalidSqlDefaultValue(row.children ?? []));
  }
  return invalid;
}

export function findRowInTree(
  rows: FriendlyFieldRow[],
  rowId: string,
): FriendlyFieldRow | null {
  for (const row of rows) {
    if (row.id === rowId) return row;
    const childMatch = findRowInTree(row.children, rowId);
    if (childMatch) return childMatch;
  }
  return null;
}

export function updateRowInTree(
  rows: FriendlyFieldRow[],
  rowId: string,
  updater: (row: FriendlyFieldRow) => FriendlyFieldRow,
): FriendlyFieldRow[] {
  return rows.map((row) => {
    if (row.id === rowId) {
      return updater(row);
    }
    if (row.children.length > 0) {
      return {
        ...row,
        children: updateRowInTree(row.children, rowId, updater),
      };
    }
    return row;
  });
}

export function removeRowFromTree(
  rows: FriendlyFieldRow[],
  rowId: string,
): FriendlyFieldRow[] {
  return rows
    .filter((row) => row.id !== rowId)
    .map((row) =>
      row.children.length > 0
        ? { ...row, children: removeRowFromTree(row.children, rowId) }
        : row,
    );
}

export function addChildToRow(
  rows: FriendlyFieldRow[],
  parentId: string,
  child: FriendlyFieldRow,
): FriendlyFieldRow[] {
  return rows.map((row) => {
    if (row.id === parentId) {
      return { ...row, children: [...row.children, child] };
    }
    if (row.children.length > 0) {
      return {
        ...row,
        children: addChildToRow(row.children, parentId, child),
      };
    }
    return row;
  });
}
